import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../../services';
import UniversalSearchBar, { SearchQuery } from '../../content/UniversalSearchBar';
import FilterSidebar, { ActiveFilters } from '../../content/FilterSidebar';
import { ContentTile } from '../../content/ContentTile';
import { ContentRow } from '../../content/ContentRow';
import { ContentLink } from '../../content/ContentLink';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../../context/OrganizationContext';
import { useRBAC } from '../../../context/RBACContext';
import { usePublicLinkAccess } from '../../../context/PublicLinkAccessContext';
import { publicLinksService } from '../../../services/public-links/public-links.service';

// Define component props based on the schema
interface ViewContentPickProps {
  contentIds?: string[];
  onContentIdsChange?: (ids: string[]) => void;
  displayMode?: 'grid' | 'list' | 'tile' | 'minimal';
  bannerStyle?: 'thumbnails' | 'illustrations';
  maxItems?: number;
  showMetadata?: boolean;
  showIcon?: boolean;
  showActions?: boolean;
  showRating?: boolean;
  showDescription?: boolean;
  enableContentSelector?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  borderRadius?: number;
  padding?: number;
  margin?: number;
  gap?: number;
  columns?: number;
  itemBackgroundColor?: string;
  itemBorderColor?: string;
  itemBorderWidth?: number;
  itemBorderRadius?: number;
  itemPadding?: number;
  itemElevation?: number;
  width?: string;
  height?: string;
  containerHeight?: string; // Height of the scrollable container
  enableScroll?: boolean; // Enable scrolling when content exceeds containerHeight
  isEditing?: boolean;
  componentId?: string; // pageDoc block id; used in public mode for self-resolution
}

// Error boundary wrapper for safe rendering
class ViewContentPickErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: '' };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error: error?.message || 'An error occurred' };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('ViewContentPick error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error">
          Failed to load content picker: {this.state.error}
        </Alert>
      );
    }

    return this.props.children;
  }
}

const ViewContentPickInner: React.FC<ViewContentPickProps> = ({
  contentIds = [],
  onContentIdsChange,
  displayMode = 'grid',
  bannerStyle = 'illustrations',
  maxItems = 12,
  showIcon = true,
  showDescription = true,
  showMetadata = true,
  showRating = true,
  showActions = true,
  enableContentSelector = true,
  columns = 3,
  gap = 2,
  itemBackgroundColor,
  itemBorderColor,
  itemBorderWidth,
  itemBorderRadius,
  itemPadding,
  itemElevation = 1,
  backgroundColor,
  borderColor = '#e0e0e0',
  borderWidth = 0,
  borderStyle = 'none',
  borderRadius,
  padding,
  margin,
  width = '100%',
  height,
  containerHeight,
  enableScroll = false,
  isEditing = false,
  componentId,
}) => {
  const navigate = useNavigate();
  const { currentOrganizationId, currentTeamId } = useOrganization();
  const { aggregatedPermissions } = useRBAC();
  const publicLinkCreds = usePublicLinkAccess();
  const isPublicMode = !!publicLinkCreds;
  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Add global error handler for this component
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.warn('Unhandled promise rejection in ViewContentPick:', event.reason);
      event.preventDefault(); // Prevent the default error handling
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    text: '',
    filters: {
      funnelStages: [],
      personas: [],
      tags: [],
    },
  });
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    mimeTypes: [],
    tagGroupFilters: {},
    systemTags: [],
    personalTags: [],
    tags: [],
    authors: [],
    organizations: [],
    teams: [],
    dateRange: {
      start: null,
      end: null,
    },
    rating: {
      min: 0,
      max: 5,
    },
    bookmarkedOnly: false,
    statuses: [],
    personalTagsOnly: false,
    systemTagsOnly: false,
    funnelStages: [],
    personas: [],
  });
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(contentIds));
  const [searchLoading, setSearchLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Function to fetch content (can be called for retry)
  const fetchContentData = async () => {
      if (contentIds.length === 0) {
        setContent([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setWarning(null);

      // Public mode: resolve via publicLinksService.resolveComponent
      if (isPublicMode && publicLinkCreds && componentId) {
        try {
          // The context carries one of two credential shapes: top-level
          // (token + password OR shareToken) for visitors of the share itself,
          // or nested (accessToken) for visitors of a click-through view.
          // Either is forwarded as-is; the gateway routes to the appropriate
          // backend auth mode. On password-protected shares, the shareToken
          // stands in for the password when React state has lost it (e.g.
          // back-nav restoring the page).
          const res = await publicLinksService.resolveComponent(
            publicLinkCreds.linkId,
            componentId,
            'accessToken' in publicLinkCreds && publicLinkCreds.accessToken
              ? { accessToken: publicLinkCreds.accessToken }
              : {
                  token: publicLinkCreds.token,
                  password: publicLinkCreds.password,
                  shareToken: publicLinkCreds.shareToken,
                }
          );
          setContent(res.items.map((it: any) => ({
            id: it.id,
            title: it.title,
            description: it.description || '',
            mimeType: it.mimeType || '',
            thumbnailUrl: it.thumbnailUrl || '',
            kind: it.kind,
            accessToken: it.accessToken,
            // Tile-rendering metadata
            fileSize: it.fileSize,
            fileSizeBytes: it.fileSizeBytes,
            fileName: it.fileName,
            createdAt: it.createdAt,
            status: it.status,
            viewCount: it.viewCount ?? 0,
            downloadCount: it.downloadCount ?? 0,
            averageRating: it.averageRating ?? 0,
            tagIds: it.tagIds ?? [],
            category: it.category,
            authorName: it.authorName,
          })));
        } catch (err: any) {
          console.error('ViewContentPick: failed to load public component', err);
          setError(err?.message || 'Failed to load content');
          setContent([]);
        } finally {
          setLoading(false);
        }
        return;
      }

      const errors: string[] = [];
      const failedIds: string[] = [];

      // Create promises with individual error handling
      const contentPromises = contentIds.slice(0, maxItems).map((id) => {
        return new Promise<any>((resolve) => {
          // Always resolve, never reject to prevent unhandled rejections
          contentService.getContent(id, currentTeamId)
            .then((response) => {
              // Extract the actual content from the nested structure
              if (response?.data?.content) {
                resolve(response.data.content);
              } else if (response?.content) {
                resolve(response.content);
              } else if (response?.data) {
                resolve(response.data);
              } else {
                resolve(response);
              }
            })
            .catch((error: any) => {
              // Handle different error types gracefully
              const status = error?.response?.status || error?.status;

              if (status === 403) {
                errors.push(`Access denied`);
                failedIds.push(id);
                console.warn(`Access denied for content ${id}`);
              } else if (status === 404) {
                errors.push(`Content not found`);
                failedIds.push(id);
                console.warn(`Content not found: ${id}`);
              } else {
                errors.push(`Failed to load`);
                failedIds.push(id);
                console.warn(`Failed to fetch content ${id}:`, error?.message || error);
              }
              // Always resolve with null on error
              resolve(null);
            });
        });
      });

      try {
        const results = await Promise.all(contentPromises);
        const validContent = results.filter(item => item !== null);

        // Set appropriate error message if some content couldn't be loaded
        if (errors.length > 0 && validContent.length === 0) {
          // All content failed to load
          const uniqueErrors = [...new Set(errors)];
          setError(`Unable to load content: ${uniqueErrors.join(', ')}`);
        } else if (errors.length > 0 && validContent.length > 0) {
          // Some content loaded, some failed - show warning
          setWarning(`${errors.length} item(s) could not be loaded`);
          console.warn(`Failed to load content IDs: ${failedIds.join(', ')}`);
        }

        // Update state
        setContent(validContent);
      } catch (err: any) {
        // This should rarely happen now since we're handling errors in the promises
        console.error('Unexpected error in fetchContent:', err);
        const errorMessage = err?.message || 'Failed to load content';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
  };

  // useEffect to fetch content when contentIds change
  useEffect(() => {
    fetchContentData().catch(err => {
      console.error('Error in fetchContentData:', err);
      setLoading(false);
    });
  }, [contentIds, maxItems]);

  // Perform search (with pagination support)
  const performSearch = async (page: number = 1, append: boolean = false) => {
    if (!append) {
      setSearchLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      // Ensure we have organization and team context
      if (!currentOrganizationId || !currentTeamId) {
        console.warn('No organization or team context available for search');
        if (!append) {
          setSearchResults([]);
          setTotalResults(0);
        }
        return;
      }

      const searchParams: any = {
        query: searchQuery.text,
        organization_id: currentOrganizationId,
        team_id: currentTeamId,
        sort_by: 'updatedAt',
        sort_order: 'desc',
        page: page,
        limit: 30,
        fuzzy_search: true,
        include_archived: false,
        user_permissions: aggregatedPermissions, // Pass user permissions for RBAC filtering
      };

      // Add ALL filters from activeFilters using correct API parameter names
      
      // MIME Types
      if (activeFilters.mimeTypes?.length) {
        searchParams.mime_types = activeFilters.mimeTypes;
      }
      
      // Authors
      if (activeFilters.authors?.length) {
        searchParams.authors = activeFilters.authors;
      }
      
      // System Tags
      if (activeFilters.systemTags?.length) {
        searchParams.system_tags = activeFilters.systemTags;
      }
      
      // Personal Tags
      if (activeFilters.personalTags?.length) {
        searchParams.personal_tags = activeFilters.personalTags;
      }
      
      // Tag Group Filters
      if (activeFilters.tagGroupFilters && Object.keys(activeFilters.tagGroupFilters).length > 0) {
        searchParams.tag_group_filters = activeFilters.tagGroupFilters;
      }
      
      // Date Range
      if (activeFilters.dateRange?.start) {
        searchParams.created_after = activeFilters.dateRange.start.toISOString();
      }
      if (activeFilters.dateRange?.end) {
        searchParams.created_before = activeFilters.dateRange.end.toISOString();
      }
      
      // Rating
      if (activeFilters.rating) {
        if (activeFilters.rating.min > 1) {
          searchParams.min_rating = activeFilters.rating.min;
        }
        if (activeFilters.rating.max < 5) {
          searchParams.max_rating = activeFilters.rating.max;
        }
      }
      
      // Boolean filters
      if (activeFilters.bookmarkedOnly) {
        searchParams.bookmarked_only = true;
      }
      if (activeFilters.personalTagsOnly) {
        searchParams.personal_tags_only = true;
      }
      if (activeFilters.systemTagsOnly) {
        searchParams.system_tags_only = true;
      }
      
      console.log('Search params with filters:', searchParams);

      const response = await searchService.searchContent(searchParams);
      const results = response?.data || [];
      const pagination = response?.metadata?.pagination;
      
      if (!pagination) {
        console.error('No pagination metadata in search response:', response);
        if (!append) {
          setSearchResults(results);
          setTotalResults(results.length);
          setHasMorePages(false);
        }
        return;
      }
      
      console.log('Search response pagination:', pagination);
      
      if (append) {
        setSearchResults(prev => [...prev, ...results]);
      } else {
        setSearchResults(results);
      }
      
      setTotalResults(pagination.totalCount);
      setCurrentPage(pagination.currentPage);
      setHasMorePages(pagination.hasNextPage);
    } catch (err: any) {
      console.warn('Search error:', err?.message || err);
      if (!append) {
        setSearchResults([]);
        setTotalResults(0);
      }
    } finally {
      setSearchLoading(false);
      setLoadingMore(false);
    }
  };

  // Track filter changes
  useEffect(() => {
    console.log('ActiveFilters changed:', activeFilters);
  }, [activeFilters]);

  // Create a stable key for all filter changes
  const filterKey = JSON.stringify(activeFilters);

  useEffect(() => {
    if (dialogOpen) {
      console.log('Triggering search with filters:', activeFilters, 'and query:', searchQuery.text);
      // Reset pagination and perform new search
      setCurrentPage(1);
      setHasMorePages(false);
      // Wrap in try-catch and handle promise
      try {
        performSearch(1, false).catch((error) => {
          console.warn('Error performing search:', error);
        });
      } catch (error) {
        console.warn('Error in search useEffect:', error);
      }
    }
  }, [searchQuery.text, dialogOpen, filterKey]);

  const handleSelectContent = (contentId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(contentId)) {
      newSelected.delete(contentId);
    } else {
      newSelected.add(contentId);
    }
    setSelectedIds(newSelected);
  };

  const handleRemoveContent = (contentId: string) => {
    try {
      if (isEditing && onContentIdsChange) {
        const newIds = contentIds.filter(id => id !== contentId);
        onContentIdsChange(newIds);
      }
    } catch (error) {
      console.error('Error removing content:', error);
    }
  };

  const handleMoveUp = (index: number) => {
    try {
      if (isEditing && onContentIdsChange && index > 0) {
        const newIds = [...contentIds];
        [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
        onContentIdsChange(newIds);
      }
    } catch (error) {
      console.error('Error moving content up:', error);
    }
  };

  const handleMoveDown = (index: number) => {
    try {
      if (isEditing && onContentIdsChange && index < contentIds.length - 1) {
        const newIds = [...contentIds];
        [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
        onContentIdsChange(newIds);
      }
    } catch (error) {
      console.error('Error moving content down:', error);
    }
  };

  const handleSaveSelection = () => {
    const newIds = Array.from(selectedIds);
    if (onContentIdsChange) {
      onContentIdsChange(newIds);
    }
    setDialogOpen(false);
  };

  // Handle scroll for infinite loading
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight;
    
    // Load more when user scrolls to 80% of the content
    if (scrollPercentage > 0.8 && hasMorePages && !loadingMore && !searchLoading) {
      performSearch(currentPage + 1, true);
    }
  };

  const handleContentClick = (contentId: string) => {
    try {
      if (!isEditing) {
        // Public mode: navigate via signed access token
        if (isPublicMode) {
          const clickedContent = content.find(c => c.id === contentId);
          if (clickedContent?.accessToken) {
            navigate(`/p/content?access=${encodeURIComponent(clickedContent.accessToken)}`);
          }
          return;
        }
        // Check if content is viewable before navigating
        const clickedContent = content.find(c => c.id === contentId);
        if (clickedContent) {
          // Don't navigate to draft content that has no file
          if (clickedContent.status === 0 || clickedContent.status === 'draft') {
            console.warn('Cannot view draft content without file');
            setWarning('This content is in draft status and cannot be viewed');
            return;
          }
        }
        navigate(`/content/${contentId}`);
      }
    } catch (error) {
      console.error('Error navigating to content:', error);
    }
  };

  const renderContentItem = (rawItem: any, index: number) => {
    const item = bannerStyle === 'illustrations'
      ? { ...rawItem, thumbnailUrl: '', thumbnail_url: '' }
      : rawItem;
    const isDraft = item.status === 0 || item.status === 1 || item.status === 'draft';
    // For search results, fileUrl may be empty but content is still viewable if private or public
    // So we check: not draft AND (has fileUrl OR status is private/public)
    const isViewable = !isDraft && (!!item.fileUrl || item.status === 'private' || item.status === 'public' || item.status === 2 || item.status === 3);

    const showPrimitive = {
      thumbnail: showIcon,
      description: showDescription,
      meta: showMetadata,
      stats: showRating,
      // actions is intentionally omitted — edit affordances pass through the
      // `actions` prop directly. When editActions is undefined the primitive
      // renders no action row; when defined it renders our edit buttons.
    };
    const handlePrimitiveClick = !isEditing && isViewable
      ? () => handleContentClick(item.id)
      : undefined;

    const editActions: React.ReactNode[] | undefined =
      isEditing && enableContentSelector
        ? [
            <Tooltip key="up" title="Move up">
              <span>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={(e) => { e.stopPropagation(); handleMoveUp(index); }}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>,
            <Tooltip key="down" title="Move down">
              <span>
                <IconButton
                  size="small"
                  disabled={index === contentIds.length - 1}
                  onClick={(e) => { e.stopPropagation(); handleMoveDown(index); }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>,
            <Tooltip key="delete" title="Remove from view">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleRemoveContent(item.id); }}
                sx={(theme) => ({ color: theme.palette.error.main })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>,
          ]
        : undefined;

    // Minimal View
    if (displayMode === 'minimal') {
      if (isEditing && enableContentSelector) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ContentLink content={item} onClick={handlePrimitiveClick} disabled={!isViewable} />
            </Box>
            {editActions}
          </Box>
        );
      }
      return (
        <ContentLink
          content={item}
          onClick={handlePrimitiveClick}
          disabled={!isViewable}
        />
      );
    }

    // List View
    if (displayMode === 'list') {
      return (
        <ContentRow
          content={item}
          show={showPrimitive}
          onClick={handlePrimitiveClick}
          actions={editActions}
        />
      );
    }

    // Tile View
    if (displayMode === 'tile') {
      return (
        <ContentTile
          content={item}
          variant="compact"
          show={showPrimitive}
          onClick={handlePrimitiveClick}
          actions={editActions}
        />
      );
    }

    // Grid View (default)
    return (
      <ContentTile
        content={item}
        variant="grid"
        show={showPrimitive}
        onClick={handlePrimitiveClick}
        actions={editActions}
      />
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            contentIds.length > 0 && (
              <Button 
                color="inherit" 
                size="small" 
                onClick={fetchContentData}
              >
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      );
    }

    if (content.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            No Content Selected
          </Typography>
          {isEditing && enableContentSelector ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click the button below to select content to display
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Select Content
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No content has been selected for display
            </Typography>
          )}
        </Paper>
      );
    }

    if (displayMode === 'list') {
      return (
        <Box>
          {content.map((item, index) => (
            <React.Fragment key={item.id}>
              {renderContentItem(item, index)}
            </React.Fragment>
          ))}
        </Box>
      );
    }

    // Minimal view - render as a simple list container
    if (displayMode === 'minimal') {
      return (
        <Paper 
          variant="outlined"
          sx={{ 
            backgroundColor: backgroundColor || 'background.paper',
            borderColor: borderColor,
            borderWidth: borderWidth,
            borderStyle: borderStyle !== 'none' ? borderStyle : undefined,
            borderRadius: `${borderRadius}px`,
            overflow: 'hidden',
          }}
        >
          {content.map((item, index) => (
            <React.Fragment key={item.id}>
              {renderContentItem(item, index)}
            </React.Fragment>
          ))}
        </Paper>
      );
    }

    // Native CSS Grid with `minmax(0, 1fr)` columns: every column is exactly
    // 1/N of the container regardless of child content width, eliminating
    // the content-sensitive overflow that MUI Grid's flex-based layout
    // exhibited on certain pages.
    const gapPx = `${(gap ?? 1) * 8}px`;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: `repeat(${columns}, minmax(0, 1fr))`,
          },
          gap: gapPx,
          width: '100%',
          minWidth: 0,
        }}
      >
        {content.map((item, index) => (
          <Box key={item.id} sx={{ minWidth: 0 }}>
            {renderContentItem(item, index)}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor,
          borderColor,
          borderWidth,
          borderStyle: borderWidth > 0 ? borderStyle : undefined,
          borderRadius,
          padding,
          margin,
          width: width || '100%',
          height: containerHeight || height,
          overflow: enableScroll ? 'auto' : 'visible',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isEditing && enableContentSelector && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">
              {content.length} item{content.length !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              {content.length > 0 ? 'Edit Selection' : 'Select Content'}
            </Button>
          </Box>
        )}
        {warning && content.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2, flexShrink: 0 }}
            onClose={() => setWarning(null)}
          >
            {warning}
          </Alert>
        )}
        <Box sx={{
          flex: enableScroll ? 1 : 'none',
          overflow: enableScroll ? 'auto' : 'visible',
          minHeight: 0,
        }}>
          {renderContent()}
        </Box>
      </Box>

      {/* Content Selection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{ 
          zIndex: 10000,
          '& .MuiDialog-paper': {
            height: '85vh',
            maxHeight: '900px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Select Content</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                label={`${selectedIds.size} selected`}
                color="primary"
                variant={selectedIds.size > 0 ? 'filled' : 'outlined'}
              />
              <IconButton onClick={() => setDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box display="flex" height="100%">
            {/* Left Panel - Filters (Collapsible) */}
            <Box 
              sx={{ 
                width: showFilters ? 280 : 0,
                flexShrink: 0,
                borderRight: showFilters ? 1 : 0,
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'width 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {showFilters && (
                <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
                  <FilterSidebar
                    activeFilters={activeFilters}
                    onChange={setActiveFilters}
                  />
                </Box>
              )}
            </Box>

            {/* Right Panel - Search and Results */}
            <Box flex={1} display="flex" flexDirection="column" p={2}>
              {/* Search Bar with Filter Toggle */}
              <Box mb={1} display="flex" gap={1}>
                <IconButton 
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ 
                    border: 1,
                    borderColor: showFilters ? 'primary.main' : 'grey.300',
                    backgroundColor: showFilters ? 'primary.light' : 'transparent',
                    '&:hover': {
                      backgroundColor: showFilters ? 'primary.light' : 'grey.100',
                    }
                  }}
                  size="small"
                >
                  <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                    <FilterListIcon fontSize="small" color={showFilters ? "primary" : "inherit"} />
                  </Tooltip>
                </IconButton>
                <Box flex={1}>
                  <UniversalSearchBar
                    onSearch={(query) => setSearchQuery(query)}
                    value={searchQuery}
                  />
                </Box>
              </Box>

              {/* Document Count */}
              {totalResults > 0 && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {totalResults} document{totalResults !== 1 ? 's' : ''} found
                    {searchResults.length < totalResults && ` (showing ${searchResults.length})`}
                  </Typography>
                </Box>
              )}

              {/* Active Filters Display */}
              {!showFilters && (activeFilters.tags?.length > 0 || 
                activeFilters.authors?.length > 0 || 
                activeFilters.organizations?.length > 0 ||
                activeFilters.mimeTypes?.length > 0) && (
                <Box mb={2} display="flex" flexWrap="wrap" gap={0.5}>
                  {activeFilters.tags?.map(tag => (
                    <Chip 
                      key={tag} 
                      label={tag} 
                      size="small" 
                      onDelete={() => setActiveFilters({
                        ...activeFilters,
                        tags: activeFilters.tags?.filter(t => t !== tag)
                      })}
                    />
                  ))}
                  {activeFilters.mimeTypes?.map(type => (
                    <Chip 
                      key={type} 
                      label={type} 
                      size="small" 
                      onDelete={() => setActiveFilters({
                        ...activeFilters,
                        mimeTypes: activeFilters.mimeTypes?.filter(t => t !== type)
                      })}
                    />
                  ))}
                  {(activeFilters.tags?.length > 0 || activeFilters.mimeTypes?.length > 0) && (
                    <Button 
                      size="small" 
                      onClick={() => setActiveFilters({
                        ...activeFilters,
                        tags: [],
                        mimeTypes: [],
                        authors: [],
                        organizations: []
                      })}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>
              )}

              {/* Search Results in List Format */}
              <Box sx={{ flex: 1, overflow: 'auto' }} onScroll={handleScroll}>
                {searchLoading && searchResults.length === 0 ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : searchResults.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={4}>
                    <Typography variant="body1" color="text.secondary">
                      Search for content to add to your selection
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {searchResults.map((item) => {
                      const isSelected = selectedIds.has(item.id);
                      const handlePrimitiveClick = () => handleSelectContent(item.id);
                      const showPrimitive = {
                        thumbnail: true,
                        description: false,
                        meta: true,
                        stats: false,
                        actions: false, // picker has no action row — selection IS the action
                      };

                      return (
                        <ContentTile
                          key={item.id}
                          content={item}
                          variant="compact"
                          show={showPrimitive}
                          onClick={handlePrimitiveClick}
                          selected={isSelected}
                        />
                      );
                    })}
                    
                    {/* Loading indicator for infinite scroll */}
                    {loadingMore && (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    
                    {/* End of results message */}
                    {!hasMorePages && searchResults.length > 0 && searchResults.length === totalResults && (
                      <Box display="flex" justifyContent="center" p={2}>
                        <Typography variant="caption" color="text.secondary">
                          All {totalResults} results loaded
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveSelection}
            variant="contained"
            disabled={selectedIds.size === 0}
          >
            Save Selection ({selectedIds.size})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Wrap with error boundary for safety
const ViewContentPick: React.FC<ViewContentPickProps> = (props) => {
  return (
    <ViewContentPickErrorBoundary>
      <ViewContentPickInner {...props} />
    </ViewContentPickErrorBoundary>
  );
};

export default ViewContentPick;